/**
 * PERSONVELGEREN — «personer i denne årgangen» (grep B1, QA13 #54 og #56; kontrakten for trinn 1 §3).
 *
 * ================================================================================================
 * ÉN KILDE FOR ALLE VELGERNE I ÅRGANGEN.
 *
 * «Hvem» på en reservasjon søkte i kontaktregisteret, der frivillige ikke står (54), og fant folk
 * som ikke er med i årgangen. Sjåføren var stab pluss programpostens folk. Vaktskuffen sendte id-er
 * uten navn, og velgeren viste id-en (56). Alle svarer nå fra den samme lista som Personer-fanen,
 * og raden bærer det en velger trenger for å skille to like navn: telefon, kategori, rolle og
 * avdeling.
 *
 * Raden har BEGGE nøklene. `contactId` er det som lagres (fremmednøklene er organisasjonens);
 * `personId` er mennesket, for tidslinjene og for å kjenne igjen samme person på tvers av hus.
 *
 * ================================================================================================
 * KATEGORIEN ER DELTAKELSENS (`participations.category`, kontrakten §2). Til den kolonnen finnes,
 * regnes den av kildene: stab eller medlem → stab, reisefølge → artist, godkjent frivillig →
 * frivillig, lagt til for hånd → stab.
 */
import { z } from 'zod';

import { cursorQuerySchema, page } from '../http/pagination.js';

/** Samme kategorier som `participations.category` (kontrakten §2), i den rekkefølgen velgeren grupperer. */
export const EDITION_PERSON_CATEGORIES = [
  'staff',
  'volunteer',
  'artist',
  'guest',
  'press',
  'supplier',
] as const;
export const editionPersonCategorySchema = z
  .enum(EDITION_PERSON_CATEGORIES)
  .meta({ id: 'EditionPersonCategory' });
export type EditionPersonCategory = (typeof EDITION_PERSON_CATEGORIES)[number];

export const editionPersonOptionSchema = z
  .object({
    personId: z.uuid(),
    /** Det som lagres — se filhodet. */
    contactId: z.uuid(),
    displayName: z.string(),
    /** Null for en gjest som ikke ser kontaktinfo (242.1), og for en som ikke har oppgitt noe. */
    phone: z.string().nullable(),
    category: editionPersonCategorySchema,
    /** Rollene i årgangen, ved navn. Tom når ingen har gitt dem en. */
    roles: z.array(z.string()),
    /** Avdelingene de er plassert i, er med i eller leder i årgangen, ved navn. */
    departmentNames: z.array(z.string()),
    /** De samme avdelingene som id-er — en gjest kan bare sette folk fra sine avdelinger på vakt (232.3). */
    departmentIds: z.array(z.uuid()),
  })
  .meta({ id: 'EditionPersonOption' });
export type EditionPersonOptionDto = z.infer<typeof editionPersonOptionSchema>;

export const EDITION_PERSON_OPTION_SORT_KEYS = ['name'] as const;

export const listEditionPersonOptionsQuerySchema = cursorQuerySchema(
  EDITION_PERSON_OPTION_SORT_KEYS,
)
  .extend({
    editionId: z.uuid(),
    /** Søker på navn, telefon, rolle og avdeling (og det Personer-fanens søk ellers dekker). */
    q: z.string().trim().min(1).max(120).optional(),
  })
  .meta({ id: 'ListEditionPersonOptionsQuery' });
export type ListEditionPersonOptionsQuery = z.infer<typeof listEditionPersonOptionsQuerySchema>;

export const editionPersonOptionListResponseSchema = page(
  editionPersonOptionSchema,
  'EditionPersonOptionListResponse',
);
export type EditionPersonOptionListResponse = z.infer<typeof editionPersonOptionListResponseSchema>;
